import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import UserDetailModal from "@/components/admin/UserDetailModal";
import { ArrowLeft, Users, Filter, Download, Eye, Edit } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface UserData {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  created_at: string;
  roles: string[];
  subscription_plan?: string;
  subscription_status?: string;
  visibility_package?: string;
  preferred_language?: string | null;
}

export default function AdminUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [subscriptionFilter, setSubscriptionFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [initialTab, setInitialTab] = useState<'details' | 'roles' | 'actions' | 'stats'>('details');

  useEffect(() => {
    loadUsers();
  }, [searchTerm]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Recupera TUTTI gli utenti già arricchiti dal backend
      const { data: allUsersData, error: usersError } = await supabase.functions.invoke(
        'get-users-admin',
        {
          body: { userIds: null } // null = tutti gli utenti
        }
      );

      if (usersError) {
        console.error('Error fetching users:', usersError);
        toast.error(t("admin.users.export.error"));
        return;
      }

      // Gli utenti sono già arricchiti con roles, subscription, profile, preferences
      const formattedUsers: UserData[] = (allUsersData?.users || []).map((user: any) => ({
        id: user.id,
        email: user.email || "N/A",
        first_name: user.first_name || null,
        last_name: user.last_name || null,
        created_at: user.created_at,
        roles: user.roles || [],
        subscription_plan: user.subscription_plan || null,
        subscription_status: user.subscription_status || null,
        visibility_package: user.visibility_package || null,
        preferred_language: user.preferred_language || null,
      }));

      // Applica filtri
      let filtered = formattedUsers;

      // Filtro per ruolo
      if (roleFilter) {
        filtered = filtered.filter((u) => u.roles.includes(roleFilter));
      }

      // Filtro per ricerca (email, nome, cognome)
      if (searchTerm) {
        filtered = filtered.filter((u) =>
          u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Filtro per periodo iscrizione
      if (dateFromFilter) {
        filtered = filtered.filter((u) => 
          new Date(u.created_at) >= new Date(dateFromFilter)
        );
      }
      if (dateToFilter) {
        filtered = filtered.filter((u) => 
          new Date(u.created_at) <= new Date(dateToFilter + 'T23:59:59')
        );
      }

      // Filtro per abbonamento
      if (subscriptionFilter === 'active') {
        filtered = filtered.filter((u) => u.subscription_status === 'active');
      } else if (subscriptionFilter === 'inactive') {
        filtered = filtered.filter((u) => !u.subscription_status || u.subscription_status !== 'active');
      }

      setUsers(filtered);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error(t("admin.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (user: UserData, tab: 'details' | 'roles' | 'actions' | 'stats' = 'details') => {
    setSelectedUser(user);
    setInitialTab(tab);
    setIsDetailModalOpen(true);
    
    try {
      const [profileRes, reviewsRes, eventsRes, companiesRes] = await Promise.all([
        supabase.from("profiles").select("*").eq("user_id", user.id).maybeSingle(),
        supabase.from("reviews").select("id", { count: 'exact', head: true }).eq("user_id", user.id),
        supabase.from("events").select("id", { count: 'exact', head: true }).eq("organizer_id", user.id),
        supabase.from("companies").select("id", { count: 'exact', head: true }).eq("owner_user_id", user.id),
      ]);
      
      setUserDetails({
        profile: profileRes.data,
        reviewsCount: reviewsRes.count || 0,
        eventsCount: eventsRes.count || 0,
        companiesCount: companiesRes.count || 0,
      });
    } catch (error) {
      console.error("Error loading user details:", error);
    }
  };

  const toggleFilters = () => {
    setIsFiltersOpen(!isFiltersOpen);
  };

  const applyFilters = () => {
    // Validazione date
    if (dateFromFilter && dateToFilter && new Date(dateFromFilter) > new Date(dateToFilter)) {
      toast.error(t("admin.users.filters.dateError"));
      return;
    }
    loadUsers();
  };

  const resetFilters = () => {
    setSearchTerm("");
    setRoleFilter("");
    setSubscriptionFilter("");
    setDateFromFilter("");
    setDateToFilter("");
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (roleFilter) count++;
    if (subscriptionFilter) count++;
    if (dateFromFilter || dateToFilter) count++;
    return count;
  };

  const formatUserType = (user: UserData): string => {
    const parts: string[] = [];
    
    // Abbonamento
    if (user.subscription_plan) {
      parts.push(user.subscription_plan.toUpperCase());
    }
    
    // Ruoli - MOSTRA TUTTI I RUOLI, non solo il primo
    if (user.roles && user.roles.length > 0) {
      const roleLabels: string[] = [];
      
      if (user.roles.includes('admin')) {
        roleLabels.push(t("admin.users.roles.admin"));
      }
      if (user.roles.includes('subscriber')) {
        roleLabels.push(t("admin.users.roles.subscriber"));
      }
      if (user.roles.includes('registered')) {
        roleLabels.push(t("admin.users.roles.registered"));
      }
      
      if (roleLabels.length > 0) {
        parts.push(roleLabels.join(', '));
      }
    } else {
      // Utente senza ruoli assegnati
      parts.push(t("admin.users.roles.registered"));
    }
    
    return parts.join(' | ') || t("admin.users.roles.registered");
  };

  const handleExportCSV = () => {
    if (users.length === 0) {
      toast.error(t("admin.users.export.noUsers"));
      return;
    }

    try {
      toast.info(t("admin.users.export.preparing"));
      
      const headers = [
        t("admin.users.export.headers.firstName"),
        t("admin.users.export.headers.lastName"),
        t("admin.users.export.headers.registrationDate"),
        t("admin.users.export.headers.email"),
        t("admin.users.export.headers.language"),
        t("admin.users.export.headers.type")
      ];
      const rows = users.map(user => [
        user.first_name || '',
        user.last_name || '',
        format(new Date(user.created_at), "dd/MM/yyyy"),
        user.email,
        user.preferred_language ? user.preferred_language.toUpperCase() : 'Auto',
        formatUserType(user)
      ]);

      // Escape quotes and wrap in quotes
      const escapeCsvCell = (cell: string) => `"${cell.replace(/"/g, '""')}"`;
      
      const csvContent = [
        headers.map(escapeCsvCell).join(','),
        ...rows.map(row => row.map(escapeCsvCell).join(','))
      ].join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `utenti_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      toast.success(t("admin.users.export.success", { count: users.length }));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("admin.users.export.error"));
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-surface rounded w-1/4"></div>
            <div className="h-96 bg-surface rounded"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        {/* Breadcrumbs */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.users.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.users.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <Users className="w-10 h-10 text-primary" />
              {t("admin.users.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("admin.users.subtitle")}</p>
          </div>
        </div>

        {/* Filters */}
        <BrutalCard>
          <div className="space-y-4">
            {/* Riga principale sempre visibile */}
            <div className="flex gap-4 items-center">
              {/* Barra ricerca estesa */}
              <div className="flex-1">
                <BrutalInput
                  placeholder={t("admin.users.search.placeholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {/* Bottone filtri con badge count */}
              <button
                onClick={toggleFilters}
                className="relative p-3 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                aria-label={t("admin.users.search.showFilters")}
              >
                <Filter className="w-5 h-5" />
                {getActiveFiltersCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-dark font-bold text-xs w-5 h-5 rounded-full flex items-center justify-center border-[2px] border-dark">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </button>

              {/* Bottone Export CSV */}
              <button
                onClick={handleExportCSV}
                className="p-3 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                aria-label={t("admin.users.search.exportCSV")}
                title={t("admin.users.search.exportCSV")}
              >
                <Download className="w-5 h-5" />
              </button>
            </div>

            {/* Pannello filtri espandibile */}
            {isFiltersOpen && (
              <div className="space-y-4 pt-4 border-t-[3px] border-dark">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Select Ruolo */}
                  <div>
                    <label className="text-sm font-bold mb-2 block">{t("admin.users.filters.role")}</label>
                    <select
                      className="w-full border-[3px] border-dark p-3 font-body text-base bg-card text-foreground focus:outline-none focus:ring-4 focus:ring-primary transition-all"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                    >
                      <option value="">{t("admin.users.filters.all")}</option>
                      <option value="registered">{t("admin.users.filters.normalUser")}</option>
                      <option value="subscriber">{t("admin.users.filters.subscriber")}</option>
                      <option value="admin">{t("admin.users.filters.admin")}</option>
                    </select>
                  </div>

                  {/* Data Da */}
                  <div>
                    <label className="text-sm font-bold mb-2 block">{t("admin.users.filters.dateFrom")}</label>
                    <input
                      type="date"
                      className="w-full border-[3px] border-dark p-3 font-body text-base bg-card text-foreground focus:outline-none focus:ring-4 focus:ring-primary transition-all"
                      value={dateFromFilter}
                      onChange={(e) => setDateFromFilter(e.target.value)}
                    />
                  </div>

                  {/* Data A */}
                  <div>
                    <label className="text-sm font-bold mb-2 block">{t("admin.users.filters.dateTo")}</label>
                    <input
                      type="date"
                      className="w-full border-[3px] border-dark p-3 font-body text-base bg-card text-foreground focus:outline-none focus:ring-4 focus:ring-primary transition-all"
                      value={dateToFilter}
                      onChange={(e) => setDateToFilter(e.target.value)}
                    />
                  </div>

                  {/* Select Abbonamento */}
                  <div>
                    <label className="text-sm font-bold mb-2 block">{t("admin.users.filters.subscription")}</label>
                    <select
                      className="w-full border-[3px] border-dark p-3 font-body text-base bg-card text-foreground focus:outline-none focus:ring-4 focus:ring-primary transition-all"
                      value={subscriptionFilter}
                      onChange={(e) => setSubscriptionFilter(e.target.value)}
                    >
                      <option value="">{t("admin.users.filters.all")}</option>
                      <option value="active">{t("admin.users.filters.active")}</option>
                      <option value="inactive">{t("admin.users.filters.inactiveOrNone")}</option>
                    </select>
                  </div>
                </div>

                {/* Bottoni azione */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={resetFilters}
                    className="px-6 py-3 border-[3px] border-dark font-heading font-bold uppercase tracking-wider bg-card text-foreground shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] hover:shadow-dark transition-all"
                  >
                    {t("admin.users.filters.reset")}
                  </button>
                  <button
                    onClick={applyFilters}
                    className="px-6 py-3 border-[3px] border-dark font-heading font-bold uppercase tracking-wider bg-primary text-dark shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] hover:shadow-dark transition-all"
                  >
                    {t("admin.users.filters.apply")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </BrutalCard>

        {/* Users Table */}
        <BrutalCard>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-b-[3px] border-dark">
                <tr>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.users.table.fullName")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.users.table.registeredOn")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.users.table.email")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.users.table.emailLanguage")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.users.table.type")}
                  </th>
                  <th className="px-6 py-4 text-left font-heading font-bold">
                    {t("admin.users.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b-[3px] border-dark hover:bg-surface"
                  >
                    <td className="px-6 py-4">
                      {user.first_name || user.last_name ? (
                        `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      ) : (
                        <span className="text-muted-foreground text-sm">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {format(new Date(user.created_at), "dd/MM/yyyy")}
                    </td>
                    <td className="px-6 py-4">{user.email}</td>
                    <td className="px-6 py-4">
                      {user.preferred_language ? (
                        <Badge variant="outline" className="uppercase">
                          {user.preferred_language === 'it' && '🇮🇹 IT'}
                          {user.preferred_language === 'en' && '🇬🇧 EN'}
                          {user.preferred_language === 'es' && '🇪🇸 ES'}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">
                          🌐 {t("admin.users.table.languageAuto")}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 flex-wrap">
                        {/* Piano abbonamento */}
                        {user.subscription_plan && (
                          <Badge variant="default" className="uppercase bg-primary text-primary-foreground">
                            {user.subscription_plan}
                          </Badge>
                        )}
                        {/* Pacchetto visibilità */}
                        {user.visibility_package && (
                          <Badge variant="outline" className="uppercase border-amber-500 text-amber-600">
                            {user.visibility_package}
                          </Badge>
                        )}
                        {/* Ruoli */}
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role) => (
                            <Badge key={role} variant="secondary" className="uppercase">
                              {role}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="secondary" className="uppercase">
                            {t("admin.users.roles.registered")}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handleViewDetails(user, 'details')}
                                className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                aria-label={t("admin.users.table.viewDetailsTooltip")}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t("admin.users.table.viewDetailsTooltip")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handleViewDetails(user, 'roles')}
                                className="p-2 border-[3px] border-dark bg-card shadow-[4px_4px_0px_0px] shadow-dark hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0px_0px] hover:shadow-dark transition-all"
                                aria-label={t("admin.users.table.editRolesTooltip")}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t("admin.users.table.editRolesTooltip")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {t("admin.users.search.noResults")}
              </div>
            )}
          </div>
        </BrutalCard>
      </div>

      <UserDetailModal
        user={selectedUser}
        userDetails={userDetails}
        isOpen={isDetailModalOpen}
        initialTab={initialTab}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedUser(null);
          setUserDetails(null);
          setInitialTab('details');
        }}
        onUserUpdated={async () => {
          // Ricarica i dati specifici dell'utente dal backend
          if (selectedUser) {
            const { data: updatedUserData } = await supabase.functions.invoke(
              "get-users-admin",
              {
                body: { userIds: [selectedUser.id] },
              }
            );

            if (updatedUserData?.users?.[0]) {
              const enrichedUser = updatedUserData.users[0];
              setSelectedUser({
                ...selectedUser,
                roles: enrichedUser.roles || [],
                subscription_plan: enrichedUser.subscription_plan || null,
                subscription_status: enrichedUser.subscription_status || null,
                visibility_package: enrichedUser.visibility_package || null,
                first_name: enrichedUser.first_name || null,
                last_name: enrichedUser.last_name || null,
                preferred_language: enrichedUser.preferred_language || null,
              });
            }
          }

          // Ricarica la tabella
          await loadUsers();
        }}
      />
    </AdminLayout>
  );
}